
import { useCallback } from 'react';
import { useProductData } from './useProductData';
import { useProductSave } from './useProductSave';
import { useProductMediaHandler } from '../../hooks/useProductMediaHandler';
import type { UseProductFormProps, UseProductFormReturn } from './types';

export const useProductForm = (props: UseProductFormProps): UseProductFormReturn => {
  const { productId, isNewProduct, shopId, organizationId } = props;
  const { loading, productState, setProductState } = useProductData(props);
  const { saving, handleSave: saveProduct, defaultFormId } = useProductSave({
    productId,
    isNewProduct,
    productState,
    onSuccess: (savedProductId) => {
      if (props.onSuccess) {
        props.onSuccess();
      }
    },
    shopId,
    organizationId
  });

  // Add media handler to properly manage media URLs
  const { handleSaveWithMedia } = useProductMediaHandler({
    mediaUrls: productState.mediaUrls,
    setMediaUrls: (urls) => setProductState(prev => ({ ...prev, mediaUrls: urls })),
    name: productState.name,
    productId: isNewProduct ? 'new' : productId,
    shopId
  });

  const createSetter = <K extends keyof typeof productState>(key: K) => {
    return (value: typeof productState[K]) => {
      setProductState(prev => ({ ...prev, [key]: value }));
    };
  };

  const handleSave = useCallback(async (updatedMediaUrls?: string[]) => {
    // Use the enhanced media handler to properly save images
    await handleSaveWithMedia((finalUrls) => saveProduct(finalUrls));
  }, [handleSaveWithMedia, saveProduct]);

  return {
    loading,
    saving,
    productId: isNewProduct ? 'new' : productId,
    handleSave,
    defaultFormId,
    
    ...productState,
    
    setShowSummary: createSetter('showSummary'),
    setName: createSetter('name'),
    setOverview: createSetter('overview'),
    setDescription: createSetter('description'),
    setPrice: createSetter('price'),
    setDiscountPrice: createSetter('discountPrice'),
    setSku: createSetter('sku'),
    setQuantity: createSetter('quantity'),
    setLowStockThreshold: createSetter('lowStockThreshold'),
    setIsOutOfStock: createSetter('isOutOfStock'),
    setIsStockManaged: createSetter('isStockManaged'),
    setMediaUrls: createSetter('mediaUrls'),
    setCategory: createSetter('category'),
    setBenefits: createSetter('benefits'),
    setFaqs: createSetter('faqs'),
    setWeight: createSetter('weight'),
    setDimensions: createSetter('dimensions'),
    setTags: createSetter('tags'),
    setIsVisible: createSetter('isVisible'),
    setStatus: createSetter('status'),
    setCurrency: createSetter('currency'),
    setFormId: createSetter('formId')
  };
};

export * from './types';
